import { useCallback, useEffect, useMemo, useState } from "react";
import type {
	ProviderCardProps,
	ProviderItem,
} from "../../core/types/tsx/components/Profesional/ProviderCard.types";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Heart, MapPin, Phone, Star } from "lucide-react";
import { getImageUri } from "../../core/utils/imageLoad";
import {
	resolveProviderReviewsCount,
	resolveProviderStats,
} from "../../core/utils/providerStats";
import { useAuthSessionContext } from "../routing/auth-session-store";
import {
	useCreateFavorite,
	useFavoritesByClient,
	useUpdateFavorite,
} from "../../hooks/control";
import { useFetch } from "../../hooks/useFetch";

const outlineButton =
	"cursor-pointer rounded-full bg-secondary px-8 py-2 text-sm font-semibold text-primary transition hover:bg-secondary-hover";
const primaryButton =
	"cursor-pointer rounded-full bg-primary px-8 py-2 text-sm font-semibold text-white transition hover:bg-primary-hover";

const toNumber = (price: string) => Number(price.replace(/[^0-9.]/g, "")) || 0;

const inRange = (range: string, price: number) => {
	const match = range.match(/\$?(\d+)\s*-\s*\$?(\d+)/);
	if (!match) return true;
	const [, low, high] = match;
	const min = Number(low);
	const max = Number(high);
	return price >= min && price <= max;
};

const uniq = (values: Array<string | undefined | null>) => {
	const out: string[] = [];
	const seen = new Set<string>();
	for (const v of values) {
		const s = (v ?? "").toString().trim();
		if (!s) continue;
		if (seen.has(s)) continue;
		seen.add(s);
		out.push(s);
	}
	return out;
};

type ProviderSubcategoryLink = {
	subcategories_services?: {
		name_subcategory?: string | null;
	} | null;
};

type ProviderCategoryLink = {
	name_category?: string | null;
	name?: string | null;
	label?: string | null;
	categoriesServices?: {
		name_category?: string | null;
	} | null;
	category?: {
		name_category?: string | null;
	} | null;
	categories?: {
		name?: string | null;
		label?: string | null;
	} | null;
	categories_providers?: {
		categories_services?: {
			name_category?: string | null;
		} | null;
	} | null;
	categories_services?: {
		name_category?: string | null;
	} | null;
	subcategories_services_providers?:
		| ProviderSubcategoryLink[]
		| ProviderSubcategoryLink
		| null;
};

type ProviderServiceLike = {
	is_active?: boolean | null;
	base_price?: number | string | null;
	name_service?: string | null;
	categories_services_providers?:
		| ProviderCategoryLink[]
		| ProviderCategoryLink
		| null;
	categoriesServices_servicesProviders?:
		| ProviderCategoryLink[]
		| ProviderCategoryLink
		| null;
	categories?: ProviderCategoryLink[] | ProviderCategoryLink | null;
};

type ProviderAddressLike = {
	street_address?: string | null;
	name_address?: string | null;
	name_country?: string | null;
	country?: {
		name_country?: string | null;
	} | null;
	states?: {
		countries?: {
			name_country?: string | null;
		} | null;
		name_state?: string | null;
		name_complete?: string | null;
	} | null;
	name_state?: string | null;
	state?: {
		name_state?: string | null;
	} | null;
	name_city?: string | null;
	city?: {
		name_city?: string | null;
	} | null;
	cities?: {
		name_city?: string | null;
	} | null;
	municipalities?: {
		name_municipality?: string | null;
	} | null;
	parishes?: {
		name_parish?: string | null;
	} | null;
};

type ProviderLike = {
	id?: number | string;
	providerId?: number | string;
	featured?: boolean;
	categoria?: string | null;
	category?: string | null;
	company_name?: string | null;
	nombre?: string | null;
	description_provider?: string | null;
	rating?: number | string | null;
	availability?: boolean | null;
	member_from?: string | null;
	addresses_user?: string | null;
	phone?: string | null;
	telefono?: string | null;
	users?: {
		names?: string | null;
		last_names?: string | null;
		image_profile?: string | null;
		addresses_user?: string | null;
		phone?: string | null;
		addresses?: ProviderAddressLike[] | null;
	} | null;
	addresses?: ProviderAddressLike[] | null;
	servicesProviders?: ProviderServiceLike[] | null;
	services_providers?: ProviderServiceLike[] | null;
	services?: ProviderServiceLike[] | null;
	portfolio_metrics?: Array<{
		orders_completed?: number | string | null;
		completed_orders?: number | string | null;
	}> | null;
	orders_completed?: number | string | null;
	completed_orders?: number | string | null;
	ordersCompleted?: number | string | null;
	completedOrders?: number | string | null;
	categories_providers?: Array<{
		categories_services?: {
			name_category?: string | null;
		} | null;
	}> | null;
};

const extractActiveProviderServices = (p: ProviderLike) => {
	const services = Array.isArray(p?.servicesProviders)
		? p.servicesProviders
		: Array.isArray(p?.services_providers)
			? p.services_providers
			: Array.isArray(p?.services)
				? p.services
				: [];

	return services.filter((service) => service?.is_active !== false);
};

const extractProviderCategories = (p: ProviderLike) => {
	if (typeof p?.categoria === "string" && p.categoria.trim())
		return uniq([p.categoria]);
	if (typeof p?.category === "string" && p.category.trim()) return uniq([p.category]);

	const names: Array<string | undefined | null> = [];
	const services = extractActiveProviderServices(p);

	for (const service of services) {
		const raw =
			service?.categoriesServices_servicesProviders ??
			service?.categories_services_providers ??
			service?.categories;
		const links = Array.isArray(raw) ? raw : raw ? [raw] : [];

		for (const link of links) {
			const cat = (
				link?.categoriesServices ??
				link?.category ??
				link?.categories_providers?.categories_services ??
				link
			) as {
				name_category?: string | null;
				name?: string | null;
				label?: string | null;
			};
			const name = cat?.name_category ?? cat?.name ?? cat?.label;
			names.push(name);
		}
	}

	const catProviders = Array.isArray(p?.categories_providers)
		? p.categories_providers
		: [];
	for (const cp of catProviders) {
		const name = cp?.categories_services?.name_category;
		names.push(name);
	}

	return uniq(names);
};

const shortenAddressText = (text: string) => {
	const segments = text.split(",").map((s) => s.trim());
	const parish = segments.find((s) => /^parroquia\b/i.test(s));
	const municipality = segments.find((s) => /^municipio\b/i.test(s));
	const parts = [parish, municipality].filter(Boolean);
	return parts.length ? parts.join(", ") : text;
};

const extractProviderLocation = (p: ProviderLike) => {
	const addressText = p?.users?.addresses_user ?? p?.addresses_user;
	if (typeof addressText === "string" && addressText.trim()) {
		return shortenAddressText(addressText.trim());
	}

	const address = Array.isArray(p?.users?.addresses)
		? p.users.addresses[0]
		: Array.isArray(p?.addresses)
			? p.addresses[0]
			: undefined;

	const parish = address?.parishes?.name_parish ?? null;
	const municipality = address?.municipalities?.name_municipality ?? null;
	const parts = uniq([parish, municipality].filter(Boolean) as string[]);
	if (parts.length) return parts.join(", ");

	const street = address?.street_address;
	const state = address?.states?.name_state ?? address?.state?.name_state;
	const country =
		address?.states?.countries?.name_country ?? address?.country?.name_country;
	const fallback = uniq([street, state, country]);
	return fallback.join(", ");
};

const extractProviderPhone = (p: ProviderLike) => {
	const phone = p?.users?.phone ?? p?.phone ?? p?.telefono;
	return typeof phone === "string" && phone.trim() ? phone.trim() : undefined;
};

const toMediaUrl = (raw?: string | null) => {
	return getImageUri(raw) ?? "";
};

const getInitials = (name?: string) => {
	if (!name) return "--";
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return "--";
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return `${parts[0][0] ?? ""}${parts[parts.length - 1][0] ?? ""}`.toUpperCase();
};

const parsePositiveId = (value: unknown): number | null => {
	if (typeof value === "number" && Number.isFinite(value) && value > 0) {
		return value;
	}

	if (typeof value === "string") {
		const parsed = Number(value);
		if (Number.isFinite(parsed) && parsed > 0) return parsed;
	}

	return null;
};

const extractClientIdFromRelation = (relation: unknown): number | null => {
	if (Array.isArray(relation)) {
		for (const item of relation) {
			const parsed = parsePositiveId((item as { id?: unknown })?.id);
			if (parsed) return parsed;
		}
		return null;
	}

	if (relation && typeof relation === "object") {
		return parsePositiveId((relation as { id?: unknown }).id);
	}

	return null;
};

function ProviderAvatar({
	avatar,
	name,
	className,
}: {
	avatar?: string;
	name: string;
	className: string;
}) {
	const [hasImageError, setHasImageError] = useState(false);
	const showImage = Boolean(avatar) && !hasImageError;

	if (showImage) {
		return (
			<img
				src={avatar}
				alt={`Foto de ${name}`}
				className={`${className} block`}
				onError={() => setHasImageError(true)}
			/>
		);
	}

	return (
		<div className='flex h-full w-full items-center justify-center bg-slate-200 text-sm font-semibold text-slate-600'>
			{getInitials(name)}
		</div>
	);
}

export function ProviderCard({
	providers: propProviders,
	selectedCategory,
	selectedRanges,
	selectedAvailability,
	featuredIds,
	fetchProviders,
}: ProviderCardProps) {
	const location = useLocation();
	const navigate = useNavigate();
	const { session } = useAuthSessionContext();
	const isProviderSession = session?.user?.type_user === "Proveedor";
	const [clientProfileId, setClientProfileId] = useState<number | null>(null);
	const { execute: executeGet } = useFetch<unknown>({
		endpoint: "/users/1",
		method: "GET",
		auto: false,
	});
	const {
		data: favoriteList,
		execute: refreshFavorites,
		loading: isFavoritesLoading,
	} = useFavoritesByClient(clientProfileId ?? 0);
	const { create: createFavorite, loading: isCreateFavoriteLoading } =
		useCreateFavorite();
	const { toggle: toggleFavoriteStatus, loading: isToggleFavoriteLoading } =
		useUpdateFavorite(0);
	const [providers, setProviders] = useState<ProviderItem[]>([]);
	const [loading, setLoading] = useState(false);
	const isFavoriteActionLoading =
		isFavoritesLoading || isCreateFavoriteLoading || isToggleFavoriteLoading;
	const featuredIdSet = useMemo(
		() => new Set((featuredIds || []).map((id) => String(id))),
		[featuredIds],
	);

	const favoritesByProviderId = useMemo(() => {
		const byProvider = new Map<number, { favoriteId: number; isFavorite: boolean }>();

		if (!Array.isArray(favoriteList)) return byProvider;

		for (const favorite of favoriteList) {
			const providerId = parsePositiveId(favorite?.providers?.id);
			const favoriteId = parsePositiveId(favorite?.id);
			if (!providerId || !favoriteId) continue;

			byProvider.set(providerId, {
				favoriteId,
				isFavorite: Boolean(favorite.is_favorite),
			});
		}

		return byProvider;
	}, [favoriteList]);

	const getCategoryBadgeText = (item: ProviderItem) => {
		const categories = Array.isArray(item.categories)
			? item.categories.filter((category) => category && category.trim().length > 0)
			: (item.category || "")
					.split(",")
					.map((category) => category.trim())
					.filter(Boolean);

		if (categories.length === 0) return "Sin categoría";
		if (categories.length <= 2) return categories.join(", ");
		return `${categories.slice(0, 2).join(", ")}...`;
	};

	const resolveClientProfileId = useCallback(async () => {
		if (!session?.user?.id) return null;

		const userPayload = await executeGet({
			endpoint: `/users/${session.user.id}`,
			method: "GET",
		});

		const payloadRecord = (userPayload ?? {}) as Record<string, unknown>;
		const nestedUser = payloadRecord.user;
		const userRecord =
			nestedUser && typeof nestedUser === "object"
				? (nestedUser as Record<string, unknown>)
				: payloadRecord;

		return (
			extractClientIdFromRelation(userRecord.clients) ??
			extractClientIdFromRelation(payloadRecord.clients)
		);
	}, [executeGet, session?.user?.id]);

	useEffect(() => {
		let cancelled = false;

		const syncClientProfileId = async () => {
			if (
				!session?.user?.id ||
				session?.user?.type_user !== "Cliente"
			) {
				if (!cancelled) setClientProfileId(null);
				return;
			}

			try {
				const resolved = await resolveClientProfileId();
				if (!cancelled) setClientProfileId(resolved);
			} catch {
				if (!cancelled) setClientProfileId(null);
			}
		};

		void syncClientProfileId();

		return () => {
			cancelled = true;
		};
	}, [
		resolveClientProfileId,
		session?.user?.id,
		session?.user?.type_user,
	]);

	const handleToggleFavorite = useCallback(
		async (item: ProviderItem) => {
			const providerId = parsePositiveId(item.id);
			if (!providerId) return;

			if (isProviderSession) return;

			if (!session?.user?.id) {
				navigate("/login", {
					state: {
						redirectTo: `${location.pathname}${location.search}`,
					},
				});
				return;
			}

			let ensuredClientId = clientProfileId;
			if (!ensuredClientId) {
				ensuredClientId = await resolveClientProfileId();
				setClientProfileId(ensuredClientId);
			}

			if (!ensuredClientId) return;

			const existingFavorite = favoritesByProviderId.get(providerId);

			if (existingFavorite?.favoriteId) {
				await toggleFavoriteStatus(
					!existingFavorite.isFavorite,
					existingFavorite.favoriteId,
				);
			} else {
				await createFavorite({
					client_id: ensuredClientId,
					provider_id: providerId,
				});
			}

			await refreshFavorites({});
		},
		[
			clientProfileId,
			createFavorite,
			favoritesByProviderId,
			isProviderSession,
			location.pathname,
			location.search,
			navigate,
			refreshFavorites,
			resolveClientProfileId,
			session?.user?.id,
			toggleFavoriteStatus,
		],
	);

	// Mapeo de Provider (admin) a ProviderItem (card)
	const mapProviderToItem = useCallback(
		(p: ProviderLike): ProviderItem => {
			const rawId = parsePositiveId(p.id ?? p.providerId) ?? 0;
			const isFeatured =
				featuredIdSet.size > 0
					? featuredIdSet.has(String(rawId))
					: p.featured === true;
			const categories = extractProviderCategories(p);
			const locationLabel = extractProviderLocation(p);
			const phone = extractProviderPhone(p);
			const avatar = toMediaUrl(p?.users?.image_profile ?? null);

			const services = extractActiveProviderServices(p);
			const basePrices = services
				.map((service) => Number(service?.base_price))
				.filter((price: number) => Number.isFinite(price) && price > 0);
			const minPrice = basePrices.length > 0 ? Math.min(...basePrices) : 100;

			const metrics = Array.isArray(p?.portfolio_metrics)
				? p.portfolio_metrics[0]
				: undefined;
			const stats = resolveProviderStats(p);
			const reviewsCount = resolveProviderReviewsCount(p);
			const completedRaw =
				metrics?.orders_completed ??
				metrics?.completed_orders ??
				p?.orders_completed ??
				p?.completed_orders ??
				p?.ordersCompleted ??
				p?.completedOrders;
			const hasCompletedMetric =
				completedRaw !== null &&
				completedRaw !== undefined &&
				completedRaw !== "";

			const fullName = [p?.users?.names, p?.users?.last_names]
				.filter(Boolean)
				.join(" ")
				.trim();

			return {
				id: rawId,
				name: fullName || p.company_name || p.nombre || "",
				rating: String(p.rating ?? "4.5"),
				reviews: reviewsCount,
				completed: stats.completedOrders,
				priceFrom: `$${minPrice.toFixed(2)}`,
				category: categories.join(", "),
				categories,
				location: locationLabel,
				phone,
				avatar,
				availability: p.availability ? "Hoy" : "No disponible",
				desc: p.description_provider || "",
				completedLabel: hasCompletedMetric
					? `Trabajos completados: ${stats.completedOrders}`
					: p.member_from
						? `Miembro desde: ${new Date(p.member_from).toLocaleDateString()}`
						: "",
				featured: isFeatured,
			};
		},
		[featuredIdSet],
	);

	useEffect(() => {
		if (propProviders && propProviders.length > 0) {
			setProviders(
				propProviders.map((provider) => mapProviderToItem(provider as ProviderLike)),
			);
			return;
		}
		let mounted = true;
		async function loadProviders() {
			if (!fetchProviders) return;
			setLoading(true);
			try {
				const data = await fetchProviders();
				if (mounted && Array.isArray(data)) {
					setProviders(
						data.map((provider) => mapProviderToItem(provider as ProviderLike)),
					);
				}
			} catch (_error) {
				if (mounted) setProviders([]);
			} finally {
				if (mounted) setLoading(false);
			}
		}
		loadProviders();
		return () => {
			mounted = false;
		};
	}, [fetchProviders, mapProviderToItem, propProviders]);

	const featured: ProviderItem[] = providers.filter((p) => p.featured);
	const listings: ProviderItem[] = providers.filter((p) => !p.featured);

	const passesFilters = (item: ProviderItem) => {
		const price = toNumber(item.priceFrom);
		const matchesCategory =
			selectedCategory === "Todos" ||
			item.category === selectedCategory ||
			(Array.isArray(item.categories) &&
				item.categories.includes(selectedCategory));
		const matchesRange =
			selectedRanges.length === 0 ||
			selectedRanges.some((range) => inRange(range, price));
		const matchesAvailability =
			selectedAvailability.length === 0 ||
			selectedAvailability.includes(item.availability);
		return matchesCategory && matchesRange && matchesAvailability;
	};

	const filteredFeatured = featured.filter(passesFilters);
	const filteredListings = listings.filter(passesFilters);
	const noResults =
		!loading && filteredFeatured.length === 0 && filteredListings.length === 0;

	const [page, setPage] = useState(1);
	const pageSize = 4;
	const totalPages = Math.ceil(filteredListings.length / pageSize);
	const paginatedProviders = filteredListings.slice(
		(page - 1) * pageSize,
		page * pageSize,
	);

	const hire = (item: ProviderItem) => {
		if (isProviderSession) {
			return;
		}

		if (!session?.user?.id) {
			navigate("/login", {
				state: {
					redirectTo: `/proveedor/${item.id}`,
					redirectState: { provider: item, openHireModal: true },
				},
			});
			return;
		}
		navigate(`/proveedor/${item.id}`, {
			state: { provider: item, openHireModal: true },
		});
	};

	return (
		<div className='space-y-5 font-sans text-slate-900 sm:space-y-6'>
			{loading && (
				<div className='w-full rounded-xl border border-slate-200 bg-white p-4 text-center text-slate-600 sm:p-5'>
					<p className='text-base font-semibold text-slate-900'>
						Cargando proveedores...
					</p>
				</div>
			)}
			{noResults && (
				<div className='w-full rounded-xl border border-slate-200 bg-white p-4 text-center text-slate-600 sm:p-5'>
					<p className='text-base font-semibold text-slate-900'>Lo sentimos</p>
					<p className='text-sm'>
						Por ahora no hay proveedores disponibles con estos filtros.
					</p>
				</div>
			)}

			{filteredFeatured.length > 0 && (
				<div className='w-full space-y-3'>
					<h1 className='mb-4 text-xl font-bold sm:mb-7'>Populares</h1>

					<div className='grid w-full grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3'>
						{filteredFeatured.map((item, i) => {
							const providerId = parsePositiveId(item.id);
							const isFav = providerId
								? Boolean(
										favoritesByProviderId.get(providerId)?.isFavorite,
									)
								: false;

							return (
								<article
									key={item.id ?? i}
									className='flex w-full cursor-pointer flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-primary/40 hover:shadow-md sm:p-5'
									onClick={() =>
										navigate(`/proveedor/${item.id}`, {
											state: { provider: item },
										})
									}
								>
									<div
										className='flex w-full items-center justify-between gap-2 sm:justify-end'
										onClick={(e) => e.stopPropagation()}
									>
										<span className='max-w-full truncate rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-primary sm:max-w-[78%]'>
											{getCategoryBadgeText(item)}
										</span>
										{!isProviderSession ? (
											<button
												type='button'
												className={`cursor-pointer transition ${isFav ? "text-red-500" : "text-slate-400 hover:text-red-500"}`}
												aria-label='Marcar como favorito'
												disabled={isFavoriteActionLoading}
												onClick={() =>
													void handleToggleFavorite(item)
												}
											>
												<Heart
													size={18}
													strokeWidth={2}
													className={
														isFav ? "fill-red-500" : ""
													}
												/>
											</button>
										) : null}
									</div>

									<div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
										<div className='mx-auto h-20 w-20 shrink-0 overflow-hidden rounded-full border border-slate-200 bg-slate-100 sm:mx-0 sm:h-16 sm:w-16'>
											<ProviderAvatar
												avatar={item.avatar}
												name={item.name}
												className='h-full w-full object-cover'
											/>
										</div>
										<div className='flex min-w-0 flex-col gap-1 text-center sm:text-left'>
											<div className='flex items-center justify-center gap-1 sm:justify-start'>
												<h3 className='truncate text-base font-semibold text-slate-900 sm:text-lg'>
													{item.name}
												</h3>
												<CheckCircle2 className='h-4 w-4 text-primary' />
											</div>
											<div className='flex flex-wrap items-center justify-center gap-2 text-xs text-slate-500 sm:justify-start'>
												<span className='inline-flex items-center gap-1'>
													<Star className='h-3.5 w-3.5 fill-primary text-primary' />
													{item.rating}
												</span>
												<span className='text-slate-400'>
													({item.reviews} reseñas)
												</span>
											</div>
										</div>
									</div>

									<div className='flex items-center justify-center gap-2 text-xs text-slate-500 sm:justify-start'>
										<MapPin className='h-3.5 w-3.5' />
										<span>{item.location || "-"}</span>
									</div>

									{item.phone ? (
										<div className='flex items-center justify-center gap-2 text-xs text-slate-500 sm:justify-start'>
											<Phone className='h-3.5 w-3.5' />
											<span>{item.phone}</span>
										</div>
									) : null}

									<p className='text-base font-semibold text-primary'>
										Desde {item.priceFrom}
									</p>

									<div className='text-sm text-slate-500'>
										{item.completedLabel ? (
											<p className='text-slate-400'>
												{item.completedLabel}
											</p>
										) : null}
									</div>

									<div
										className='flex items-center justify-between gap-2'
										onClick={(e) => e.stopPropagation()}
									>
										<button
											type='button'
											className={`${outlineButton} ${
												isProviderSession
													? "cursor-not-allowed opacity-60"
													: ""
											}`}
											disabled={isProviderSession}
											onClick={() => hire(item)}
										>
											Contratar
										</button>
										<Link
											to={`/proveedor/${item.id}`}
											state={{ provider: item }}
											className={primaryButton}
										>
											Ver
										</Link>
									</div>
								</article>
							);
						})}
					</div>
				</div>
			)}

			<div className='grid w-full grid-cols-1 gap-6'>
				{paginatedProviders.map((item, i) => {
					const providerId = parsePositiveId(item.id);
					const isFav = providerId
						? Boolean(favoritesByProviderId.get(providerId)?.isFavorite)
						: false;
					return (
						<article
							key={item.id ?? i}
							className='relative flex w-full cursor-pointer flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-primary/40 hover:shadow-md sm:flex-row'
							onClick={() =>
								navigate(`/proveedor/${item.id}`, {
									state: { provider: item },
								})
							}
						>
							<div
								className='relative flex items-center justify-between gap-2 sm:absolute sm:left-3 sm:right-3 sm:top-3 sm:justify-end'
								onClick={(e) => e.stopPropagation()}
							>
								<span className='max-w-full truncate rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-primary sm:max-w-[72%]'>
									{getCategoryBadgeText(item)}
								</span>

								{!isProviderSession ? (
									<button
										type='button'
										className={`cursor-pointer transition ${isFav ? "text-red-500" : "text-slate-400 hover:text-red-500"}`}
										aria-label='Marcar como favorito'
										disabled={isFavoriteActionLoading}
										onClick={() => void handleToggleFavorite(item)}
									>
										<Heart
											size={18}
											strokeWidth={2}
											className={isFav ? "fill-red-500" : ""}
										/>
									</button>
								) : null}
							</div>

							<div className='mx-auto aspect-square w-full max-w-60 overflow-hidden rounded-lg border border-slate-200 bg-slate-100 sm:mx-0 sm:self-stretch sm:min-h-24 sm:min-w-24 sm:max-w-36'>
								<ProviderAvatar
									avatar={item.avatar}
									name={item.name}
									className='h-full w-full object-cover'
								/>
							</div>

							<div className='flex flex-1 flex-col gap-2 text-center sm:text-left'>
								<div className='flex items-center justify-center gap-2 text-sm font-semibold text-slate-900 sm:justify-start'>
									{item.name}
									<CheckCircle2 className='h-4 w-4 text-primary' />
								</div>

								<div className='flex flex-wrap items-center justify-center gap-2 text-xs text-slate-500 sm:justify-start'>
									<span className='inline-flex items-center gap-1'>
										<Star className='h-3.5 w-3.5 fill-primary text-primary' />
										{item.rating}
										<span className='text-slate-400'>
											({item.reviews} reseñas)
										</span>
									</span>
									<span className='inline-flex items-center gap-1'>
										<MapPin className='h-3.5 w-3.5' />
										{item.location || "-"}
									</span>
								</div>

								{item.phone ? (
									<div className='flex flex-wrap items-center justify-center gap-2 text-xs text-slate-500 sm:justify-start'>
										<span className='inline-flex items-center gap-1'>
											<Phone className='h-3.5 w-3.5' />
											{item.phone}
										</span>
									</div>
								) : null}

								<div
									className='flex flex-col gap-3 sm:flex-row sm:items-center'
									onClick={(e) => e.stopPropagation()}
								>
									<p className='text-center text-base font-semibold text-primary sm:text-left'>
										Desde {item.priceFrom}
									</p>
									<div className='flex flex-col gap-2 sm:ml-auto sm:flex-row sm:items-center'>
										<button
											type='button'
											className={`${outlineButton} ${
												isProviderSession
													? "cursor-not-allowed opacity-60"
													: ""
											}`}
											disabled={isProviderSession}
											onClick={() => hire(item)}
										>
											Contratar
										</button>
										<Link
											to={`/proveedor/${item.id}`}
											state={{ provider: item }}
											className={primaryButton}
										>
											Ver
										</Link>
									</div>
								</div>

								<div className='flex flex-wrap items-center justify-center gap-4 text-xs text-slate-400 sm:justify-start'>
									<span>Trabajos Completados: {item.completed}</span>
								</div>
							</div>
						</article>
					);
				})}
			</div>

			{totalPages > 1 && (
				<div className='mb-19 mt-12 flex flex-wrap items-center justify-center gap-2'>
					<button
						className='cursor-pointer rounded-full border border-slate-300 bg-[#fcfcfc] px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-primary hover:bg-primary hover:text-white'
						disabled={page === 1}
						onClick={() => setPage(page - 1)}
					>
						Anterior
					</button>
					{[...Array(totalPages)].map((_, idx) => (
						<button
							key={idx + 1}
							className={`cursor-pointer rounded-full border border-slate-300 bg-[#fcfcfc] px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-primary hover:bg-primary hover:text-white ${page === idx + 1 ? "bg-primary text-white" : ""}`}
							onClick={() => setPage(idx + 1)}
						>
							{idx + 1}
						</button>
					))}
					<button
						className='cursor-pointer rounded-full border border-slate-300 bg-[#fcfcfc] px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-primary hover:bg-primary hover:text-white'
						disabled={page === totalPages}
						onClick={() => setPage(page + 1)}
					>
						Siguiente
					</button>
				</div>
			)}
		</div>
	);
}
